import { activeRecordingTransportAtom } from '@/atoms/chronicle';
import { errorStartingRecordingAtom } from '@/atoms/sessionAtoms';
import { logger } from '@/utils/logger/logger';
import { type RecordingStopPurpose } from '@heidi/capture/ports';
import {
  AudioStorageRecoveryFailed,
  CreateRecordingIdFailed,
  DeviceInUseError,
  DexieStartError,
  MicPermissionError,
  NoAudioDeviceError,
  RecordingActiveInAnotherTabError,
  StartRecordingFailed,
  TabSharingError,
  TooLowStorageError,
} from '@heidi/capture/types';
import { toast } from '@heidi/design-system/toast';
import { sanitizeError } from '@heidi/runtime/logger';
import { activeSessionIdAtom, notFoundSessionIds } from '@heidi/session/data';
import { Dexie } from 'dexie';
import { getDefaultStore, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';
import { isSessionNotFoundError } from './errors';

const jotaiStore = getDefaultStore();

// Classify at the app logger boundary without loading Capture's audio engine
// just to sanitize an error. The runtime projection drops opaque error text.
const getSafeErrorFields = (error: unknown) => ({
  errorName: error instanceof Error ? error.name : typeof error,
  ...sanitizeError(error),
});

interface UseOnErrorOptions {
  sessionId: string;
  onInterrupt: () => void;
}

export const useOnError = (options?: UseOnErrorOptions) => {
  const { t } = useTranslation();
  const intl = useIntl();
  const router = useRouter();
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const setActiveRecordingTransport = useSetAtom(activeRecordingTransportAtom);
  const sessionId = options?.sessionId;
  const onInterrupt = options?.onInterrupt;

  return useCallback(
    (err: unknown, stopPurpose?: RecordingStopPurpose) => {
      if (isSessionNotFoundError(err)) {
        if (stopPurpose !== 'start-compensation') {
          if (!sessionId || !onInterrupt) {
            logger.error({
              event_type: 'SESSION_NOT_FOUND_NO_HANDLER',
              details: getSafeErrorFields(err),
            });
            return;
          }
          // Only the first ordinary error triggers interruption. Compensation
          // must not consume this dedup key or suppress a later ordinary error.
          if (notFoundSessionIds.has(sessionId)) return;
          notFoundSessionIds.add(sessionId);
          onInterrupt();
        }
        toast({
          tone: 'destructive',
          title: t('navigation.sessionNotFound'),
          description: t('navigation.sessionNotFoundDescription'),
        });
        // Retain presentation, but not interruption, navigation or guard release
        // for this specific compensating stop before cessation is confirmed.
        if (stopPurpose === 'start-compensation') return;
        setActiveSessionId(null);
        // Interrupted recording ends here — release the transport stamp so a
        // stale 'desktop-mic' cannot misroute the next recording.
        setActiveRecordingTransport(null);
        void router.push('/new-session');
        return;
      }

      if (err instanceof AudioStorageRecoveryFailed) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'Recording couldn\u2019t start',
            id: 'Vm0RyIr3Kn',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Heidi couldn\u2019t safely finish clearing local audio. Wait a moment and try again. If this continues, reload Heidi.',
            id: 'egDoKwLIq7',
          }),
          duration: 10000,
        });
      } else if (err instanceof RecordingActiveInAnotherTabError) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'Recording already in progress',
            id: 'XhO6uCCMuC',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Heidi is recording in another tab. Stop the recording there, or close that tab, then try again.',
            id: 'VkmJvsIDu5',
          }),
          duration: 10000,
        });
      } else if (err instanceof CreateRecordingIdFailed) {
        toast({
          tone: 'destructive',
          title: t('sessionRecordingErrors.createRecordingIdFailedTitle'),
          description: t('sessionRecordingErrors.createRecordingIdFailedDescription'),
          duration: 10000,
        });
      } else if (err instanceof StartRecordingFailed) {
        toast({
          tone: 'destructive',
          title: t('sessionRecordingErrors.startRecordingFailedTitle'),
          description: t('sessionRecordingErrors.startRecordingFailedDescription'),
          duration: 10000,
        });
      } else if (err instanceof MicPermissionError) {
        jotaiStore.set(errorStartingRecordingAtom, () => ({
          isOpen: true,
          error: t('sessionRecordingErrors.micPermissionError'),
        }));
      } else if (err instanceof DeviceInUseError) {
        jotaiStore.set(errorStartingRecordingAtom, () => ({
          isOpen: true,
          error: intl.formatMessage({
            defaultMessage:
              'Your microphone is unavailable. It may be in use by another application or experiencing a device issue. Close other apps using it, check its connection, and try again.',
            id: 'wofLnb7AoM',
          }),
        }));
      } else if (err instanceof NoAudioDeviceError) {
        jotaiStore.set(errorStartingRecordingAtom, () => ({
          isOpen: true,
          error: t('sessionRecordingErrors.noAudioDeviceError'),
        }));
      } else if (err instanceof TooLowStorageError) {
        toast({
          tone: 'destructive',
          title: t('sessionRecordingErrors.tooLowStorageErrorTitle'),
          description: t('sessionRecordingErrors.tooLowStorageErrorDescription'),
          duration: 10000,
        });
      } else if (err instanceof DexieStartError) {
        toast({
          tone: 'destructive',
          title: t('sessionRecordingErrors.dexieStartErrorTitle'),
          description: t('sessionRecordingErrors.dexieStartErrorDescription'),
          duration: 10000,
        });
      } else if (err instanceof TabSharingError) {
        toast({
          tone: 'destructive',
          title: t('sessionRecordingErrors.tabSharingErrorTitle'),
          description: t('sessionRecordingErrors.tabSharingAudioDescription'),
          duration: 10000,
        });
      } else if (err instanceof Dexie.DexieError) {
        // Defence in depth for any unrecognised Dexie error: surface an
        // actionable toast rather than logging silently, or the user is left
        // watching a recording indicator that vanished.
        logger.error({
          event_type: 'DEXIE_RECORDING_ERROR',
          details: getSafeErrorFields(err),
        });
        toast({
          tone: 'destructive',
          title: t('sessionRecordingErrors.dexieStorageErrorTitle'),
          description: t('sessionRecordingErrors.dexieStorageErrorDescription'),
          duration: 15000,
        });
      } else {
        logger.error({
          event_type: 'UNHANDLED_RECORDING_ERROR',
          details: getSafeErrorFields(err),
        });
      }
    },
    [
      t,
      intl,
      sessionId,
      onInterrupt,
      router,
      setActiveSessionId,
      setActiveRecordingTransport,
    ]
  );
};
